import React, { useState, useMemo } from "react";
import { useQuery } from "@apollo/client";
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  FilterFn,
  SortingState,
} from "@tanstack/react-table";
import { useAuth } from "../AuthContext";
import { ALL_QUOTES_QUERY } from "../graphql/quotes";
import { ALL_DRAFT_QUOTES_QUERY } from "../graphql/draftQuotes";
import QuoteDialog from "../components/QuoteDialog";
import Table from "../components/Table";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SyncIcon from "@mui/icons-material/Sync";
import CircularProgress from "@mui/material/CircularProgress";
import { useQuickBooksSync } from "../hooks/useQuickBooksSync";

interface Quote {
  id: number;
  name: string;
  status: string;
  createdAt: string;
  dueAt: string | null;
  notes: string | null;
  qbEstimateId: string | null;
  qbSyncedAt: string | null;
  qbEmailStatus: string | null;
  jobByJobId: { id: number; name: string; customerByCustomerId: { id: number; name: string } };
  addressByAddressId: {
    id: number;
    street: string;
    city: string;
    state: string;
    zip: string;
  } | null;
  quoteLinesByQuoteId: {
    nodes: Array<{
      archivedAt: string | null;
      quoteLineItemsByQuoteLineId: {
        nodes: Array<{ quantity: string | number; unitPrice: string | number; archivedAt: string | null }>;
      };
    }>;
  };
}

interface DraftQuote {
  id: number;
  name: string;
  createdAt: string;
  finalizedQuoteId: number | null;
  jobByJobId: { id: number; name: string; customerByCustomerId: { id: number; name: string } };
}

const statusColorMap: Record<string, { bg: string; text: string }> = {
  pending: { bg: "#f3f4f6", text: "#374151" },
  accepted: { bg: "#d1fae5", text: "#065f46" },
  rejected: { bg: "#fee2e2", text: "#dc2626" },
  closed: { bg: "#ede9fe", text: "#5b21b6" },
};

const quoteTotalCents = (quote: Quote): number => {
  return quote.quoteLinesByQuoteId.nodes
    .filter((line) => !line.archivedAt)
    .reduce((sum, line) => {
      const lineSubtotal = line.quoteLineItemsByQuoteLineId.nodes
        .filter((item) => !item.archivedAt)
        .reduce((itemSum, item) => itemSum + Number(item.quantity) * Number(item.unitPrice), 0);
      return sum + lineSubtotal;
    }, 0);
};

const quoteFilterFn: FilterFn<Quote> = (row, _columnId, filterValue) => {
  if (!filterValue) return true;
  const s = String(filterValue).toLowerCase();
  return (
    row.original.name.toLowerCase().includes(s) ||
    row.original.jobByJobId.customerByCustomerId.name.toLowerCase().includes(s) ||
    row.original.status.toLowerCase().includes(s)
  );
};

const draftFilterFn: FilterFn<DraftQuote> = (row, _columnId, filterValue) => {
  if (!filterValue) return true;
  const s = String(filterValue).toLowerCase();
  return (
    row.original.name.toLowerCase().includes(s) ||
    row.original.jobByJobId.customerByCustomerId.name.toLowerCase().includes(s)
  );
};

const quoteColumnHelper = createColumnHelper<Quote>();
const draftColumnHelper = createColumnHelper<DraftQuote>();

// Static columns with no closure dependencies live at module scope.
const quoteNumberColumn = quoteColumnHelper.accessor("id", {
  id: "quoteNumber",
  header: "Quote #",
  cell: (info) => `#${info.getValue()}`,
  size: 90,
});

const quoteNameColumn = quoteColumnHelper.accessor("name", {
  header: "Name",
  cell: (info) => (
    <a
      href={`/quotes/${info.row.original.id}`}
      style={{ color: "#3b82f6", textDecoration: "none", fontWeight: 600 }}
    >
      {info.getValue()}
    </a>
  ),
  size: 200,
});

const quoteCustomerColumn = quoteColumnHelper.accessor(
  (row) => row.jobByJobId.customerByCustomerId.name,
  {
    id: "customer",
    header: "Customer",
    cell: (info) => info.getValue(),
    size: 180,
  },
);

const quoteJobColumn = quoteColumnHelper.accessor((row) => row.jobByJobId.name, {
  id: "job",
  header: "Job",
  cell: (info) => info.getValue(),
  size: 160,
});

const quoteTotalColumn = quoteColumnHelper.accessor((row) => quoteTotalCents(row), {
  id: "total",
  header: "Total",
  cell: (info) => `$${(info.getValue() / 100).toFixed(2)}`,
  size: 110,
});

const quoteQbEstimateColumn = quoteColumnHelper.accessor("qbEstimateId", {
  id: "qbEstimateId",
  header: "QB Estimate #",
  cell: (info) => info.getValue() || "—",
  size: 120,
});

const quoteDueDateColumn = quoteColumnHelper.accessor("dueAt", {
  header: "Due Date",
  cell: (info) => {
    const date = info.getValue();
    return date ? new Date(date).toLocaleDateString() : "—";
  },
  size: 120,
});

const quoteCreatedColumn = quoteColumnHelper.accessor("createdAt", {
  header: "Created",
  cell: (info) => new Date(info.getValue()).toLocaleDateString(),
  size: 120,
});

const draftColumns = [
  draftColumnHelper.accessor("name", {
    header: "Name",
    cell: (info) => (
      <a
        href={`/draft-quotes/${info.row.original.id}`}
        style={{ color: "#3b82f6", textDecoration: "none", fontWeight: 600 }}
      >
        {info.getValue()}
      </a>
    ),
    size: 200,
  }),
  draftColumnHelper.accessor((row) => row.jobByJobId.customerByCustomerId.name, {
    id: "customer",
    header: "Customer",
    cell: (info) => info.getValue(),
    size: 180,
  }),
  draftColumnHelper.accessor((row) => row.jobByJobId.name, {
    id: "job",
    header: "Job",
    cell: (info) => (
      <a href={`/jobs/${info.row.original.jobByJobId.id}`} style={{ color: '#374151', textDecoration: 'none' }}>
        {info.getValue()}
      </a>
    ),
    size: 160,
  }),
  draftColumnHelper.accessor("finalizedQuoteId", {
    header: "Status",
    cell: (info) => {
      const fqId = info.getValue();
      return fqId ? (
        <span
          className="status-badge"
          style={{ backgroundColor: "#d1fae5", color: "#065f46" }}
        >
          Finalized
        </span>
      ) : (
        <span
          className="status-badge"
          style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
        >
          In Progress
        </span>
      );
    },
    size: 120,
  }),
  draftColumnHelper.accessor("createdAt", {
    header: "Created",
    cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    size: 120,
  }),
];

type Tab = "quotes" | "drafts";

export default function Quotes() {
  const { user } = useAuth();
  const [showDialog, setShowDialog] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [jobFilter, setJobFilter] = useState("all");
  const [sorting, setSorting] = useState<SortingState>([{ id: "createdAt", desc: true }]);
  const [activeTab, setActiveTab] = useState<Tab>("quotes");
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [draftQuotes, setDraftQuotes] = useState<DraftQuote[]>([]);

  const { loading: quotesLoading, error: quotesError, refetch: refetchQuotes } = useQuery(
    ALL_QUOTES_QUERY,
    {
      variables: { domainId: user?.domain_id || 0 },
      skip: !user?.domain_id,
      fetchPolicy: "network-only",
      onCompleted: (data) => setQuotes(data?.allQuotes?.nodes || []),
    },
  );

  const { loading: draftsLoading, error: draftsError } = useQuery(
    ALL_DRAFT_QUOTES_QUERY,
    {
      variables: { domainId: user?.domain_id || 0 },
      skip: !user?.domain_id,
      fetchPolicy: "network-only",
      onCompleted: (data) => setDraftQuotes(data?.allDraftQuotes?.nodes || []),
    },
  );

  const {
    sendToQuickBooks,
    checkQuickBooksStatus,
    pollOpenQuotes,
    isPending: isQbPending,
    isPollingOpen,
  } = useQuickBooksSync();
  const [qbMessage, setQbMessage] = useState<string | null>(null);
  const [qbMessageIsError, setQbMessageIsError] = useState(false);

  // Apollo doesn't reliably re-fire useQuery's onCompleted callback on a
  // refetch() call, so setQuotes must be driven from refetch()'s own
  // resolved data rather than assuming onCompleted will run again.
  const refreshQuotes = async () => {
    const { data } = await refetchQuotes();
    setQuotes(data?.allQuotes?.nodes || []);
  };

  const handleSendToQuickBooks = async (quoteId: number) => {
    setQbMessage(null);
    const result = await sendToQuickBooks(quoteId);
    setQbMessageIsError(!result.ok);
    setQbMessage(result.message);
    // Refresh regardless of ok: a "sync failed" response can still mean the
    // estimate was created before a later step errored, so the table should
    // reflect whatever actually landed server-side.
    await refreshQuotes();
  };

  const handleCheckQuickBooksStatus = async (quoteId: number) => {
    setQbMessage(null);
    const result = await checkQuickBooksStatus(quoteId);
    setQbMessageIsError(!result.ok);
    setQbMessage(result.message);
    await refreshQuotes();
  };

  const handlePollOpenQuotes = async () => {
    setQbMessage(null);
    const result = await pollOpenQuotes();
    setQbMessageIsError(!result.ok);
    setQbMessage(result.message);
    // Refresh even on partial failure (chunkErrors) — some quotes may have
    // still updated successfully and should show up in the table.
    await refreshQuotes();
  };

  // Depends on qbAction closures above, so it's built here rather than at module scope.
  const quoteStatusColumn = quoteColumnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      // quote.status is already lowercase (PostGraphile only uppercases enum
      // values containing symbols; plain labels like "sent" pass through
      // unchanged) — this call is a defensive no-op.
      const status = info.getValue().toLowerCase();
      const colors = statusColorMap[status] || statusColorMap.pending;
      const q = info.row.original;
      const pending = isQbPending(q.id);
      const iconStyle: React.CSSProperties = {
        background: "none",
        border: "none",
        cursor: pending ? "default" : "pointer",
        color: "#9ca3af",
        padding: 2,
        display: "inline-flex",
      };
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            className="status-badge"
            style={{ backgroundColor: colors.bg, color: colors.text }}
          >
            {status}
          </span>
          {q.qbEstimateId && q.qbEmailStatus === "EmailSent" && (
            <span
              style={{ fontSize: 11, color: "#6b7280", whiteSpace: "nowrap" }}
              title="This estimate has been emailed to the customer via QuickBooks"
            >
              Emailed
            </span>
          )}
          {q.qbEstimateId ? (
            <button
              onClick={() => handleCheckQuickBooksStatus(q.id)}
              disabled={pending}
              title={
                pending
                  ? "Checking QuickBooks status…"
                  : `Check QuickBooks status (synced as estimate #${q.qbEstimateId})`
              }
              style={iconStyle}
            >
              {pending ? (
                <CircularProgress size={14} thickness={5} sx={{ color: "#9ca3af" }} />
              ) : (
                <SyncIcon sx={{ fontSize: 16 }} />
              )}
            </button>
          ) : status !== "rejected" ? (
            <button
              onClick={() => handleSendToQuickBooks(q.id)}
              disabled={pending}
              title={pending ? "Sending to QuickBooks…" : "Send to QuickBooks"}
              style={iconStyle}
            >
              {pending ? (
                <CircularProgress size={14} thickness={5} sx={{ color: "#9ca3af" }} />
              ) : (
                <CloudUploadIcon sx={{ fontSize: 16 }} />
              )}
            </button>
          ) : null}
        </div>
      );
    },
    size: 140,
  });

  const quoteColumns = [
    quoteNumberColumn,
    quoteNameColumn,
    quoteCustomerColumn,
    quoteJobColumn,
    quoteStatusColumn,
    quoteTotalColumn,
    quoteQbEstimateColumn,
    quoteDueDateColumn,
    quoteCreatedColumn,
  ];

  const jobsById = new Map<number, string>();
  (activeTab === "drafts" ? draftQuotes : quotes).forEach((q) => jobsById.set(q.jobByJobId.id, q.jobByJobId.name));
  const jobOptions = Array.from(jobsById, ([id, name]) => ({ id, name }));

  // useReactTable's `data` option must stay reference-stable when nothing
  // relevant changed, or its internal auto-reset logic loops forever
  // reacting to what looks like new data on every render (a documented
  // TanStack Table requirement, not general-purpose memoization).
  const filteredQuotes = useMemo(
    () => (jobFilter === "all" ? quotes : quotes.filter((q) => q.jobByJobId.id === Number(jobFilter))),
    [quotes, jobFilter]
  );
  const filteredDraftQuotes = useMemo(
    () => (jobFilter === "all" ? draftQuotes : draftQuotes.filter((q) => q.jobByJobId.id === Number(jobFilter))),
    [draftQuotes, jobFilter]
  );

  const quoteTable = useReactTable({
    data: filteredQuotes,
    columns: quoteColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: quoteFilterFn,
    state: { globalFilter, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    initialState: { pagination: { pageSize: 20, pageIndex: 0 } },
  });

  const draftTable = useReactTable({
    data: filteredDraftQuotes,
    columns: draftColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: draftFilterFn,
    state: { globalFilter, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    initialState: { pagination: { pageSize: 20, pageIndex: 0 } },
  });

  const handleDialogComplete = (result: { quoteId: number }) => {
    setShowDialog(false);
    window.location.href = `/draft-quotes/${result.quoteId}`;
  };

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    setGlobalFilter("");
    setJobFilter("all");
  };

  const loading = activeTab === "quotes" ? quotesLoading : draftsLoading;
  const error = activeTab === "quotes" ? quotesError : draftsError;
  const hasNone =
    activeTab === "quotes" ? quotes.length === 0 : draftQuotes.length === 0;
  const noFilterMatches =
    !hasNone &&
    (activeTab === "quotes" ? filteredQuotes.length === 0 : filteredDraftQuotes.length === 0);

  const tabStyle = (tab: Tab): React.CSSProperties => ({
    padding: "8px 20px",
    border: "none",
    borderBottom:
      activeTab === tab ? "2px solid #3b82f6" : "2px solid transparent",
    background: "none",
    fontSize: "14px",
    fontWeight: activeTab === tab ? 700 : 500,
    color: activeTab === tab ? "#3b82f6" : "#6b7280",
    cursor: "pointer",
    transition: "all 0.15s",
  });

  const activeTableState = activeTab === "quotes" ? quoteTable : draftTable;
  const { pageIndex, pageSize } = activeTableState.getState().pagination;
  const pageCount = activeTableState.getPageCount();
  const filteredCount = activeTableState.getFilteredRowModel().rows.length;
  const showPagination = filteredCount > pageSize;

  return (
    <div className="quotes-page">
      <div className="page-header">
        <div>
          <h1>Quotes</h1>
          <p className="subtitle">Create and manage project quotes</p>
        </div>
        <button className="primary-button btn-icon" onClick={() => setShowDialog(true)}>
          <AddIcon fontSize="small" /> New Quote
        </button>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e5e7eb",
          marginBottom: 20,
        }}
      >
        <div style={{ display: "flex" }}>
          <button
            style={tabStyle("drafts")}
            onClick={() => handleTabChange("drafts")}
          >
            Draft Quotes {draftQuotes.length > 0 && `(${draftQuotes.length})`}
          </button>
          <button
            style={tabStyle("quotes")}
            onClick={() => handleTabChange("quotes")}
          >
            Finalized Quotes {quotes.length > 0 && `(${quotes.length})`}
          </button>
        </div>
        {activeTab === "quotes" && (
          <button
            onClick={handlePollOpenQuotes}
            disabled={isPollingOpen}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              marginBottom: 8,
              background: "none",
              border: "1px solid #d1d5db",
              borderRadius: 6,
              fontSize: 13,
              fontWeight: 600,
              color: "#374151",
              cursor: isPollingOpen ? "default" : "pointer",
            }}
          >
            {isPollingOpen ? (
              <CircularProgress size={14} thickness={5} sx={{ color: "#6b7280" }} />
            ) : (
              <SyncIcon sx={{ fontSize: 16 }} />
            )}
            Poll Open Quotes
          </button>
        )}
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error.message}
          </div>
        )}

        {loading && hasNone && (
          <div className="loading-message">Loading...</div>
        )}

        {!loading && hasNone && (
          <div className="empty-state">
            <div className="empty-icon"><AssignmentOutlinedIcon fontSize="inherit" /></div>
            {activeTab === "drafts" ? (
              <>
                <h2>No draft quotes yet</h2>
                <p>Click "New Quote" to start your first draft</p>
              </>
            ) : (
              <>
                <h2>No finalized quotes yet</h2>
                <p>Start a draft quote and finalize it to see it here</p>
              </>
            )}
          </div>
        )}

        {!hasNone && (
          <>
            {activeTab === "quotes" && qbMessage && (
              <div
                style={{
                  margin: "0 0 12px",
                  padding: "8px 12px",
                  borderRadius: 4,
                  fontSize: 13,
                  background: qbMessageIsError ? "#fee2e2" : "#d1fae5",
                  color: qbMessageIsError ? "#dc2626" : "#065f46",
                }}
              >
                {qbMessage}
              </div>
            )}
            <div className="filter-bar" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <input
                type="text"
                className="filter-input"
                placeholder={
                  activeTab === "drafts"
                    ? "Search by name, job, or customer..."
                    : "Search by name, job, customer, or status..."
                }
                value={globalFilter}
                onChange={(e) => {
                  setGlobalFilter(e.target.value);
                  activeTableState.setPageIndex(0);
                }}
              />
              <select
                value={jobFilter}
                onChange={(e) => {
                  setJobFilter(e.target.value);
                  activeTableState.setPageIndex(0);
                }}
                style={{ padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, background: "#fff" }}
              >
                <option value="all">All Jobs</option>
                {jobOptions.map((j) => (
                  <option key={j.id} value={j.id}>{j.name}</option>
                ))}
              </select>
              <select
                value={sorting[0]?.desc ? "desc" : "asc"}
                onChange={(e) => {
                  setSorting([{ id: "createdAt", desc: e.target.value === "desc" }]);
                  activeTableState.setPageIndex(0);
                }}
                style={{ padding: "9px 12px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, background: "#fff" }}
              >
                <option value="desc">Created — Newest First</option>
                <option value="asc">Created — Oldest First</option>
              </select>
            </div>

            {noFilterMatches ? (
              <div className="empty-state">
                <div className="empty-icon"><AssignmentOutlinedIcon fontSize="inherit" /></div>
                <h2>No {activeTab === "drafts" ? "draft " : ""}quotes match these filters</h2>
              </div>
            ) : activeTab === "drafts" ? (
              <Table table={draftTable} isLoading={loading} />
            ) : (
              <Table table={quoteTable} isLoading={loading} />
            )}

            {showPagination && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 12,
                  marginTop: 16,
                  fontSize: 14,
                  color: "#6b7280",
                }}
              >
                <span>
                  {pageIndex * pageSize + 1}–
                  {Math.min((pageIndex + 1) * pageSize, filteredCount)} of{" "}
                  {filteredCount}
                </span>
                <button
                  onClick={() => activeTableState.previousPage()}
                  disabled={!activeTableState.getCanPreviousPage()}
                  style={{
                    padding: "4px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: 4,
                    background: "#fff",
                    cursor: activeTableState.getCanPreviousPage()
                      ? "pointer"
                      : "not-allowed",
                    color: activeTableState.getCanPreviousPage()
                      ? "#374151"
                      : "#d1d5db",
                  }}
                >
                  <ChevronLeftIcon fontSize="small" /> Prev
                </button>
                <span>
                  Page {pageIndex + 1} of {pageCount}
                </span>
                <button
                  onClick={() => activeTableState.nextPage()}
                  disabled={!activeTableState.getCanNextPage()}
                  style={{
                    padding: "4px 12px",
                    border: "1px solid #d1d5db",
                    borderRadius: 4,
                    background: "#fff",
                    cursor: activeTableState.getCanNextPage()
                      ? "pointer"
                      : "not-allowed",
                    color: activeTableState.getCanNextPage()
                      ? "#374151"
                      : "#d1d5db",
                  }}
                >
                  Next <ChevronRightIcon fontSize="small" />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showDialog && (
        <QuoteDialog
          onComplete={handleDialogComplete}
          onClose={() => setShowDialog(false)}
        />
      )}
    </div>
  );
}
